// =====================================================
// HOOK: useGlobalTimeline
// Description: Hook pour gérer les événements timeline globaux avec pagination
// =====================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSupabase } from './useSupabase';
import { useUsers } from '../contexts/UsersContext';
import type { TimelineEvent } from '../types/timeline';

// ✅ PAGINATION : Nombre d'événements par page
const EVENTS_PER_PAGE = 50;

export function useGlobalTimeline() {
  const supabase = useSupabase();
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const offsetRef = useRef(0); // ✅ Utiliser useRef au lieu d'un état
  const [hasMore, setHasMore] = useState(false);

  // Récupérer tous les événements de l'organisation avec pagination
  const fetchEvents = useCallback(
    async (append: boolean = false) => {
      try {
        // Ne mettre loading à true que pour le chargement initial
        if (!append) {
          setLoading(true);
        }
        setError(null);

        const currentOffset = append ? offsetRef.current : 0;

        // Récupérer TOUS les événements (pas de filtre utilisateur) avec pagination
        const { data, error: fetchError } = await supabase
          .from('timeline_events')
          .select('*')
          .order('created_at', { ascending: false })
          .range(currentOffset, currentOffset + EVENTS_PER_PAGE - 1);

        if (fetchError) throw fetchError;

        const fetchedEvents = (data as any[]) || [];

        // Enrichir les événements avec les informations utilisateur depuis le cache
        const enrichedEvents = fetchedEvents.map((event) => ({
          ...event,
          created_by_user:
            event.created_by && usersMap[event.created_by] ? usersMap[event.created_by] : null,
        }));

        // Déterminer s'il y a plus d'événements
        setHasMore(fetchedEvents.length === EVENTS_PER_PAGE);

        // Ajouter ou remplacer les événements
        if (append) {
          setEvents((prev) => [...prev, ...enrichedEvents]);
          offsetRef.current = currentOffset + fetchedEvents.length;
        } else {
          setEvents(enrichedEvents);
          offsetRef.current = fetchedEvents.length;
        }
      } catch (err: any) {
        // Ne logger que si ce n'est pas une erreur de permissions
        if (err?.code !== 'PGRST301') {
          console.error('Erreur lors du chargement de la timeline globale:', err);
          setError(err as Error);
        } else {
          // Pour les erreurs 403, continuer sans erreur
          setEvents([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [supabase, usersMap]
  );

  // Charger les événements au montage du composant
  useEffect(() => {
    fetchEvents(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger plus d'événements (pagination)
  const loadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchEvents(true);
    }
  }, [loading, hasMore, fetchEvents]);

  // Rafraîchir manuellement la liste (reset offset et recharge le premier lot)
  const refresh = useCallback(async () => {
    offsetRef.current = 0;
    await fetchEvents(false);
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    hasMore,
    refresh,
    loadMore,
  };
}
